import { composeFetch } from '@library/http'
import {
  addPath,
  composeRequest,
  setHeader,
  setHost,
  setMethod,
  setQueryParams,
} from '@library/http/requests'

export const ERROR_CODES = Object.freeze({
  NOT_FOUND: 'NOT_FOUND',
  TIMEOUT: 'TIMEOUT',
  UPSTREAM_5XX: 'UPSTREAM_5XX',
  RATE_LIMITED: 'RATE_LIMITED',
  BAD_CONFIG: 'BAD_CONFIG',
} as const)

type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES]

type Success = { ok: true; data: Record<string, unknown> }
type Failure = { ok: false; code: ErrorCode; retryable: boolean; message: string }
export type ContentstackResult = Success | Failure

const RETRYABLE = new Set<ErrorCode>([
  ERROR_CODES.TIMEOUT,
  ERROR_CODES.UPSTREAM_5XX,
  ERROR_CODES.RATE_LIMITED,
])

function success(data: Record<string, unknown>): Success {
  return { ok: true, data }
}

function failure(code: ErrorCode, message: string): Failure {
  return { ok: false, code, retryable: RETRYABLE.has(code), message }
}

function getConfig() {
  return {
    host: process.env.CONTENTSTACK_HOST || 'https://eu-cdn.contentstack.com',
    apiKey: process.env.CONTENTSTACK_API_KEY || '',
    deliveryToken: process.env.CONTENTSTACK_DELIVERY_TOKEN || '',
    environment: process.env.CONTENTSTACK_ENVIRONMENT || 'production',
    timeoutMs: Number(process.env.CONTENTSTACK_TIMEOUT_MS) || 5000,
  }
}

function classifyHttpStatus(status: number): ErrorCode {
  if (status === 404) return ERROR_CODES.NOT_FOUND
  if (status === 401 || status === 403) return ERROR_CODES.BAD_CONFIG
  if (status === 429) return ERROR_CODES.RATE_LIMITED
  if (status >= 500) return ERROR_CODES.UPSTREAM_5XX

  return ERROR_CODES.BAD_CONFIG
}

type FetchEntriesInput = {
  contentType: string
  pageUrl: string
  host: string
  apiKey: string
  deliveryToken: string
  environment: string
  signal: AbortSignal
}

const fetchEntries = composeFetch<FetchEntriesInput>(
  composeRequest<FetchEntriesInput>(
    setHost((input) => input.host),
    setMethod('GET'),
    addPath((input) => `v3/content_types/${input.contentType}/entries`),
    setQueryParams((input) => ({
      query: JSON.stringify({ url: input.pageUrl }),
      environment: input.environment,
      limit: '1',
    })),
    setHeader('api_key', (input) => input.apiKey),
    setHeader('access_token', (input) => input.deliveryToken),
    (req, input) => ({ ...req, signal: input.signal }),
  ),
)

export async function fetchByUrl(
  contentType: string,
  pageUrl: string,
): Promise<ContentstackResult> {
  const config = getConfig()

  if (!config.apiKey || !config.deliveryToken) {
    return failure(ERROR_CODES.BAD_CONFIG, 'ContentStack credentials are not configured')
  }

  const signal = AbortSignal.timeout(config.timeoutMs)

  try {
    const response = await fetchEntries({
      contentType,
      pageUrl,
      host: config.host,
      apiKey: config.apiKey,
      deliveryToken: config.deliveryToken,
      environment: config.environment,
      signal,
    })

    if (!response.ok) {
      return failure(
        classifyHttpStatus(response.status),
        `ContentStack responded with ${response.status}`,
      )
    }

    const json = await response.json()
    const entries = json['entries']
    const entry = Array.isArray(entries)
      ? (entries[0] as Record<string, unknown> | undefined)
      : undefined

    if (!entry) {
      return failure(ERROR_CODES.NOT_FOUND, `No entry found for URL: ${pageUrl}`)
    }

    return success(entry)
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      return failure(ERROR_CODES.TIMEOUT, 'ContentStack request timed out')
    }

    return failure(ERROR_CODES.UPSTREAM_5XX, 'ContentStack request failed')
  }
}
